import { keepPreviousData, useQuery, type UseQueryResult } from "@tanstack/react-query";
import { apiFetch, type ApiError } from "../../lib/api.ts";
import { useTimeRange } from "../../state/time-range.tsx";
import type { SystemHealth } from "./vocab.ts";

/**
 * The read for the System health screen (task 11.15; design 11 operator-API System row, 12.2
 * "Control plane health"). ONE operation, role `viewer`, taken straight from `@oathera/contract/api`
 * via `vocab.ts` and never redefined here:
 *
 * - `GET /api/v1/system/health?from=…&to=…&source_id=…&instance_id=…&cursor&limit` →
 *   {@link SystemHealth}: the health SNAPSHOT (container state, queue depth/age, dead-letter depth,
 *   State-Gateway state, shard latency, ingest rates/drops, hub connections, verification lag) PLUS
 *   the SEPARATE, paged `processing_failures` list keyed by source/instance/batch-seq.
 *
 * This is a REST SNAPSHOT (design 12.3 "REST for the health snapshot"): there is NO `system` live
 * stream topic in the contract (`StreamTopic` is only `events | metrics | sources | alerts`), so the
 * gateway state and hub connections come from this snapshot and refresh on the poll, not a push. The
 * read is WINDOWED (it accepts `from`/`to`), so — exactly like the other windowed screens — the
 * window is resolved from the shared time-range and pausing freezes the query: live mode buckets
 * "now" to a 10 s grid to keep the query key stable between ticks, and polls; frozen (paused or
 * custom) uses the exact window, the key stops advancing and polling stops (design 12.2, FR-13 AC2).
 *
 * The `source_id` / `instance_id` filters narrow ONLY the `processing_failures` list (the contract
 * is explicit); the queue/gateway/runtime snapshot is unaffected by them. `cursor` / `limit` page
 * the failures. The fetch goes through `lib/api.ts`, so a cold-core 503 surfaces as a `waking`
 * {@link ApiError} and the shell shows "Waking the control plane".
 */

/** The resolved window a query ran for, so the screen can label the snapshot honestly. */
export interface SystemWindow {
  readonly fromMs: number;
  readonly toMs: number;
}

export interface UseSystemHealthOptions {
  /** Narrow the processing-failure list to this source (does not affect the queue/gateway snapshot). */
  readonly sourceId?: string | undefined;
  /** Narrow the processing-failure list to this instance. */
  readonly instanceId?: string | undefined;
  /** Opaque page cursor for `processing_failures`. */
  readonly cursor?: string | undefined;
  /** Page size for `processing_failures`. */
  readonly limit?: number;
  /** When false the read does not run (role below viewer). Defaults to true. */
  readonly enabled?: boolean;
}

export interface UseSystemHealthResult {
  readonly query: UseQueryResult<SystemHealth, ApiError>;
  /** The window the current query key was built from (what the snapshot describes). */
  readonly window: SystemWindow;
}

/**
 * Fetch the control-plane health snapshot for the current window, optionally filtered to one source
 * / instance for the processing-failure list. The query key carries the window AND the filter/paging
 * inputs so it refetches exactly when any of them changes; `staleTime` / `refetchInterval` match the
 * other windowed screens (live tolerates slight staleness and polls; frozen never goes stale and
 * does not poll).
 */
export function useSystemHealth(options: UseSystemHealthOptions = {}): UseSystemHealthResult {
  const timeRange = useTimeRange();
  const nowMs = timeRange.live ? bucket(Date.now(), 10_000) : Date.now();
  const resolved = timeRange.resolveWindow(nowMs);
  const window: SystemWindow = { fromMs: resolved.fromMs, toMs: resolved.toMs };

  const enabled = options.enabled ?? true;
  const sourceId = options.sourceId;
  const instanceId = options.instanceId;
  const cursor = options.cursor;
  const limit = options.limit;

  const query = useQuery<SystemHealth, ApiError>({
    queryKey: [
      "system-health",
      window.fromMs,
      window.toMs,
      sourceId ?? null,
      instanceId ?? null,
      cursor ?? null,
      limit ?? null,
    ],
    queryFn: ({ signal }) =>
      apiFetch<SystemHealth>("/system/health", {
        query: {
          from: window.fromMs,
          to: window.toMs,
          source_id: sourceId,
          instance_id: instanceId,
          cursor,
          limit,
        },
        signal,
      }),
    enabled,
    staleTime: timeRange.live ? 10_000 : Infinity,
    refetchInterval: timeRange.live ? 15_000 : false,
    // Keep the previous window's data while the next live window fetches, so the screen does
    // not blink through its loading state on each ~10s tick (seamless live refresh).
    placeholderData: keepPreviousData,
  });

  return { query, window };
}

/** Floor `value` to the nearest multiple of `size` (keeps the live query key stable). */
function bucket(value: number, size: number): number {
  return Math.floor(value / size) * size;
}
